/* Shared reading of the FTP listing endpoints, for every screen with a "select a
   file from the server" dropdown.
 *
 * TKT-000856: Canadian picked a Company on Bill Requests and the file dropdown
 * stayed empty. The jobs service could not read the FTP server at all, but the
 * failure was swallowed at three separate layers and arrived here as an empty
 * array - indistinguishable from a folder with nothing in it, so the screen's own
 * error branch never ran and the clerk was left with a blank control.
 *
 * The endpoints now answer `{ files, path }` on success and 502
 * `{ error, reason, path }` on a failure to reach the server. These helpers turn
 * both into something that can be put in front of a clerk, and tolerate the old
 * bare-array shape so a frontend deployed ahead of the backend still lists files.
 */

/** Files out of a listing response, old shape or new. */
export const readFtpFileList = (data) => {
  if (Array.isArray(data)) return data;
  return data && Array.isArray(data.files) ? data.files : [];
};

/** The folder that was listed, when the response names it. */
export const readFtpPath = (data) => {
  if (!data || Array.isArray(data)) return '';
  return data.path || '';
};

/**
 * What the server had to say about a listing that succeeded but holds nothing.
 *
 * Today that is "this county has no folder on that server" - a 200, because the
 * server answered; the folder simply is not there and never will be. It belongs
 * in the dropdown, not in a red flash: Tulsa raised "Unable to connect to the FTP
 * server" on every Import & Balance page load for a Landmark folder it is never
 * going to have.
 */
export const readFtpMessage = (data) => {
  if (!data || Array.isArray(data)) return '';
  return data.message || '';
};

/**
 * What to show when a listing fails. The reason is the message the FTP server
 * itself gave ("connect ECONNREFUSED 206.188.193.85:22", "Permission denied"),
 * which is the whole point - "unable to read the file list" told nobody anything.
 */
export const ftpErrorMessage = (err) => {
  const response = (err && err.response) || {};
  const data = response.data || {};
  const reason = data.reason || data.error || (err && err.message);
  return reason
    ? `Unable to connect to the FTP server: ${reason}`
    : 'Unable to connect to the FTP server.';
};

/**
 * What to show when the server answered but the folder holds nothing. Names the
 * folder, so a county that is looking in the wrong place can see that it is.
 */
export const ftpEmptyMessage = (path) => (path
  ? `No files found in ${path}`
  : 'No files found on the FTP server.');

export default {
  readFtpFileList,
  readFtpPath,
  readFtpMessage,
  ftpErrorMessage,
  ftpEmptyMessage,
};
